<template>
  <BaseSidebar
    :model-value="modelValue"
    background-color="#FFFFFF"
    width="560px"
    @update:model-value="(value: boolean) => emit('update:modelValue', value)"
  >
    <template #header="{ close }">
      <div v-if="item" class="flex flex-col gap-1 p-6 pb-3" data-testid="activity-request-drawer">
        <div class="flex items-start justify-between gap-2">
          <div>
            <p class="m-0 text-lg font-bold text-textPrimary">
              {{ item.actor?.name ?? 'Médico não informado' }}
            </p>
            <p class="m-0 text-xs text-neutral50">{{ item.actor?.crm ?? 'CRM não informado' }}</p>
          </div>
          <button
            type="button"
            class="text-sm text-neutral50"
            data-testid="activity-request-drawer-close"
            @click="close"
          >
            Fechar
          </button>
        </div>
        <p class="m-0 text-sm">
          {{ item.title }}<template v-if="item.subtitle"> · {{ item.subtitle }}</template>
        </p>
      </div>
    </template>

    <div v-if="item" class="flex flex-col gap-5 px-6 pb-6">
      <!-- Faixa previsto × efetivo × prazo -->
      <section data-testid="activity-request-strip">
        <h5 class="mb-2 text-xs font-bold uppercase text-neutral50">Previsto × efetivo × prazo</h5>
        <div class="relative h-2 rounded bg-lightGray30">
          <span
            v-for="point in strip"
            :key="point.key"
            class="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-primary30"
            :style="{ left: `${point.pct}%` }"
            :title="`${point.label} · ${formatDateTimeInStation(point.at, offset)}`"
          />
        </div>
        <ul class="m-0 mt-2 list-none p-0 text-xs text-neutral50">
          <li v-for="point in strip" :key="point.key">
            {{ point.label }}: {{ formatDateTimeInStation(point.at, offset) }}
          </li>
        </ul>
      </section>

      <!-- Tabela de 8 fatos -->
      <section>
        <h5 class="mb-2 text-xs font-bold uppercase text-neutral50">Fatos</h5>
        <table class="w-full text-sm" data-testid="activity-request-facts">
          <tbody>
            <tr v-for="fact in facts" :key="fact.label">
              <th class="w-40 py-1 pr-2 text-left align-top font-normal text-neutral50">
                {{ fact.label }}
              </th>
              <td class="py-1">{{ fact.value }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Histórico -->
      <section>
        <h5 class="mb-2 text-xs font-bold uppercase text-neutral50">Histórico da solicitação</h5>
        <p v-if="historyLoading" class="text-sm text-neutral50">Carregando histórico…</p>
        <div
          v-else-if="historyError"
          class="flex items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm"
          data-testid="activity-request-history-error"
        >
          <span>{{ historyError }}</span>
          <button
            type="button"
            class="font-bold underline"
            data-testid="activity-request-history-retry"
            @click="loadHistory()"
          >
            Tentar novamente
          </button>
        </div>
        <ol v-else class="m-0 list-none p-0" data-testid="activity-request-history">
          <li
            v-for="entry in history"
            :key="entry.key"
            class="mb-2 border-l-4 pl-3"
            :class="HISTORY_BORDER[entry.tone]"
          >
            <p class="m-0 text-xs text-neutral50">{{ entry.atLabel }}</p>
            <p class="m-0 text-sm font-bold">{{ entry.title }}</p>
            <p v-if="entry.subtitle" class="m-0 text-xs">{{ entry.subtitle }}</p>
          </li>
          <li v-if="!history.length" class="text-sm text-neutral50">Sem eventos registrados.</li>
        </ol>
      </section>

      <div class="flex justify-end" data-testid="activity-request-drawer-send-record">
        <OutlinedButton
          text="Enviar registro"
          width="160px"
          height="32px"
          @click="emit('sendRecord', item.entityId)"
        />
      </div>
    </div>
  </BaseSidebar>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import Network from '@/Network'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import BaseSidebar from '@/components/designSystem/Sidebar/BaseSidebar.vue'
import type { QueueTone } from '@/components/designSystem/QueueViews/types'
import { REQUESTED_LEG_LABELS } from '@/constants/ds-attendance.constants'
import type { ActivityRequestItem, ActivityRequestPayload } from '@/types/activity-requests.types'
import { getApiErrorMessage } from '@/utils/apiError'
import { DEFAULT_UTC_OFFSET } from '@/utils/date'

import {
  formatBRL,
  formatDateTimeInStation,
  formatDeadlineRelative,
  formatStationPeriod,
  openingRuleLabel,
  registrationModeLabel,
  sendWindowLabel,
  timezoneLabel
} from '../../utils/activity-request-format'
import {
  buildDeadlineStrip,
  mapActivityRequestTimeline
} from '../../utils/activity-request-timeline'
import type { TimelineEntry } from '../../utils/activity-request-timeline'

const props = defineProps<{
  modelValue: boolean
  item: ActivityRequestItem | null
  nowMs: number | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  sendRecord: [entityId: number]
}>()

const HISTORY_BORDER: Record<QueueTone, string> = {
  neutral: 'border-lightGray30',
  info: 'border-blue-500',
  ok: 'border-green-600',
  warn: 'border-orange-500',
  crit: 'border-red-600'
}

const offset = computed(
  () => props.item?.timezone || props.item?.payload.station.timezone || DEFAULT_UTC_OFFSET
)

const strip = computed(() => {
  if (!props.item) return []
  const p = props.item.payload
  return buildDeadlineStrip({
    expectedStart: p.station.startAt,
    expectedEnd: p.station.endAt,
    effectiveIn: p.effectiveSummary?.checkin?.value ?? null,
    effectiveOut: p.effectiveSummary?.checkout?.value ?? null,
    openedAt: p.openedAt,
    deadline: p.sendDeadlineAt ?? props.item.sla?.deadlineAt ?? null,
    nowMs: props.nowMs
  })
})

const doctorFact = (item: ActivityRequestItem): string => {
  if (!item.actor) return 'Médico não informado'
  return `${item.actor.name} · ${item.actor.ourTeam ? 'Do time' : 'Fora do time'}`
}

const ruleFact = (p: ActivityRequestPayload): string => {
  const window = sendWindowLabel(p.sendWindowHours)
  const rule = openingRuleLabel(p.openingRule)
  return window ? `${rule} · janela de ${window}` : rule
}

const deadlineFact = (deadline: string | null): string => {
  if (!deadline) return 'sem prazo'
  const relative = formatDeadlineRelative(deadline, props.nowMs)
  const absolute = `${formatDateTimeInStation(deadline, offset.value)} ${timezoneLabel(offset.value)}`
  return relative ? `${absolute} — ${relative.toLowerCase()}` : absolute
}

const paymentFact = (item: ActivityRequestItem): string => {
  const kinds = [
    item.payload.payment.hasAntecipe ? 'Antecipe' : null,
    item.payload.payment.hasInCash ? 'À vista' : null
  ].filter(Boolean)
  const parts = [
    item.value ? formatBRL(item.value.amount) : null,
    kinds.length ? `${kinds.join(' + ')} retido até o registro` : null
  ].filter(Boolean)
  return parts.join(' · ') || 'sem pagamento prioritário'
}

const facts = computed(() => {
  if (!props.item) return []
  const item = props.item
  const p = item.payload
  const period = formatStationPeriod(p.station.startAt, p.station.endAt, offset.value)
  const shift = item.location.shiftName ?? p.station.shiftName ?? 'sem turno'
  const leg = REQUESTED_LEG_LABELS[p.requestedLeg] ?? p.requestedLeg
  const mode = registrationModeLabel(p.registrationMode ?? p.requestChannel)
  return [
    {
      label: 'Plantão',
      value: `${period.weekday ? `${period.weekday}, ` : ''}${period.date} · ${period.range} · ${shift}`
    },
    { label: 'Unidade', value: item.location.healthUnitName },
    { label: 'Escala', value: item.location.scheduleName },
    { label: 'Médico', value: doctorFact(item) },
    { label: 'Perna / modalidade', value: `${leg} · ${mode}` },
    { label: 'Regra do prazo', value: ruleFact(p) },
    { label: 'Prazo', value: deadlineFact(p.sendDeadlineAt ?? item.sla?.deadlineAt ?? null) },
    { label: 'Pagamento', value: paymentFact(item) }
  ]
})

const history = ref<TimelineEntry[]>([])
const historyLoading = ref(false)
const historyError = ref<string | null>(null)
let requestSeq = 0

async function loadHistory(): Promise<void> {
  if (!props.item) return
  const seq = ++requestSeq
  historyLoading.value = true
  historyError.value = null
  try {
    const response = await Network.dsAttendance.getActivityRequestTimeline(props.item.entityId)
    if (seq !== requestSeq) return
    history.value = mapActivityRequestTimeline(response, offset.value)
  } catch (error) {
    if (seq !== requestSeq) return
    history.value = []
    historyError.value = getApiErrorMessage(
      error,
      'Não foi possível carregar o histórico da solicitação.'
    )
  } finally {
    if (seq === requestSeq) historyLoading.value = false
  }
}

watch(
  () => [props.modelValue, props.item?.entityId] as const,
  ([open, entityId]) => {
    if (open && entityId) void loadHistory()
  }
)
</script>
